import { router } from '@inertiajs/react'
import { useCallback, useRef, useState } from 'react'
import { isRateLimited } from './rate_limit'

export type LayerFetchState = 'idle' | 'pending' | 'ready' | 'failed' | 'dismissed'

// What `router.reload` will serialize into the query string. Narrow on purpose:
// an object or an array here would be serialized by axios in a shape the Rails
// side has to guess at, so a caller flattens before it gets here.
export type LayerRequestData = Record<string, string | number | boolean>

export interface LayerFetch {
  state: LayerFetchState
  load: (data?: LayerRequestData) => void
  /** Re-issues the last request, so a Retry asks for the viewport that failed. */
  retry: () => void
  dismiss: () => void
}

/**
 * One partial-reload state machine, shared by every map layer that fetches.
 *
 * The region layer grew this first, and its comment says why it is not optional:
 * "a map that silently never draws its regions is the worst of the outcomes."
 * The winery layer needs it more, not less — it refires as the camera moves, so
 * a silent failure looks like a region that genuinely holds no producers rather
 * than like a request that died.
 *
 * It is a hook rather than a copied block because P2 is adding tastings and
 * wishlist layers to the same layout, and a third hand-rolled copy of these five
 * states is how the three drift apart.
 *
 * **`onFinish` is the load-bearing guard.** A request can end having called
 * NEITHER `onSuccess` nor `onError`: the network dropped it, the server
 * answered with something that is not an Inertia response, or a navigation
 * cancelled it. Without the fallback the layer sits on `pending` for the rest
 * of the session with no banner and no retry. It reads the state to know
 * whether anything settled the request.
 *
 * **A CANCEL IS NOT A FAILURE, and is the one case told apart.** Inertia
 * cancels every reload in flight when the reader navigates to another page
 * (`asyncRequestStream.cancelInFlight`), so a reader who pans and then clicks
 * a dot before the dots were answered used to be shown "the winery layer could
 * not be loaded" for a request the app itself had withdrawn. A cancelled
 * request goes back to `idle` and says nothing; the shell, which knows a
 * navigation happened, asks for the frame again once it lands. The flag read
 * is `cancelled`, and only that one: `cancelInFlight` calls
 * `request.cancel({ cancelled: true })`, which leaves `interrupted` false, so
 * a guard written against `interrupted` would pass its test and never fire.
 *
 * `router.reload` sets `async: true` inside `doReload`, after spreading the
 * caller's options — so it always rides Inertia's async stream
 * (`maxConcurrent: Infinity`, not interruptible) and never the sync one. Two
 * layers and the search box therefore run concurrently without cancelling each
 * other, and passing `async` here would be a no-op.
 *
 * **A rate-limited reload is a failure with a Retry, and nothing more.** The
 * public atlas answers too many requests with a plain-text 429, which Inertia
 * treats as a foreign response: it calls neither `onSuccess` nor `onError` and
 * opens its error dialog over the map. The banner this hook already drives is
 * the right size for "this layer is late, ask again" — so the 429 is settled
 * as `failed` here and the dialog is declined. Any other foreign response is
 * left alone (see lib/rate_limit.ts).
 */
export default function useLayerFetch(prop: string, initial: LayerFetchState = 'idle'): LayerFetch {
  const [state, setState] = useState<LayerFetchState>(initial)
  const lastData = useRef<LayerRequestData | undefined>(undefined)
  // Which request the state currently belongs to. Because `router.reload` rides
  // the async stream, two pans put two requests in flight and they settle in
  // whatever order the network returns them — so without this, an older
  // request's `onSuccess` can land after a newer one's `onError` and flip a
  // genuine failure back to `ready`, clearing the banner and the Retry with it.
  // That is the "silently never draws" outcome this hook exists to prevent,
  // arriving through the hook itself.
  //
  // The layout already drops stale *data* by comparing the server's echoed
  // viewport against the request it issued; this is the same guard for the
  // *state*, which has no echo to compare.
  const generation = useRef(0)

  const load = useCallback(
    (data?: LayerRequestData) => {
      lastData.current = data
      generation.current += 1
      const mine = generation.current
      // Only the newest request may write state. An older one settling late is
      // not an error — it is an answer nobody is waiting for any more.
      const settle = (next: LayerFetchState | ((current: LayerFetchState) => LayerFetchState)) => {
        if (generation.current !== mine) return
        setState(next as LayerFetchState)
      }

      router.reload({
        only: [prop],
        // `preserveUrl` only when there is something to send. A layer that takes
        // no parameters reloads the URL it is already on, and adding the flag
        // there would be noise in every call the region layer makes.
        ...(data ? { data, preserveUrl: true } : {}),
        onStart: () => settle('pending'),
        onSuccess: () => settle('ready'),
        onError: () => settle('failed'),
        onHttpException: (response) => {
          if (!isRateLimited(response)) return

          settle('failed')
          return false
        },
        onFinish: (visit) => {
          if (generation.current !== mine) return

          const settled: LayerFetchState = visit?.cancelled ? 'idle' : 'failed'
          setState((current) => (current === 'pending' ? settled : current))
        },
      })
    },
    [prop],
  )

  const retry = useCallback(() => load(lastData.current), [load])
  const dismiss = useCallback(() => setState('dismissed'), [])

  return { state, load, retry, dismiss }
}
